import { forwardRef } from 'react';
import { Box, Typography, Divider, Table, TableBody, TableCell, TableHead, TableRow, Paper } from '@mui/material';
import React from 'react';

// Props: appointment, orders, hospitalInfo
interface InvoicePrintProps {
  appointment: any;
  orders: any[];
  hospitalInfo: {
    logoUrl: string;
    name: string;
    address: string;
    phone?: string;
    email?: string;
  };
}

const InvoicePrint = forwardRef<HTMLDivElement, InvoicePrintProps>(
  ({ appointment, orders, hospitalInfo }, ref) => {
    // Calculate totals
    const validOrders = orders.filter(order => {
      const financialStatus = order.financialStatus;
      return financialStatus !== 'CANCELLED' && financialStatus !== 'REFUNDED';
    });

    // Filter out INVESTIGATION items and group by item group
    const groupedOrders = validOrders
      .filter(order => order.item?.itemGroup !== 'INVESTIGATION')
      .reduce((groups: { [key: string]: any[] }, order) => {
        const groupName = order.item?.itemGroup || 'OTHERS';
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(order);
        return groups;
      }, {});

    // Define proper order for item groups (excluding INVESTIGATION)
    const groupOrder = ['CONSULTATION', 'THERAPIES', 'PHARMACY', 'CONSUMABLE', 'STATIONERY', 'OTHERS'];
    
    // Sort groups according to defined order
    const sortedGroupNames = Object.keys(groupedOrders).sort((a, b) => {
      const indexA = groupOrder.indexOf(a);
      const indexB = groupOrder.indexOf(b);
      
      // If both groups are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only one is in the order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // If neither is in the order array, sort alphabetically
      return a.localeCompare(b);
    });

    // Calculate totals excluding INVESTIGATION items
    const printableOrders = validOrders.filter(order => order.item?.itemGroup !== 'INVESTIGATION');
    const total = printableOrders.reduce((sum, o) => sum + (o.price * o.quantity - (o.discountValue || 0)), 0);
    const paid = printableOrders.reduce((sum, o) => sum + (o.paidAmount || 0), 0);
    const due = total - paid;

    return (
      <Paper ref={ref} sx={{ p: 4, width: '800px', margin: '0 auto', background: '#fff' }}>
        {/* Header with logo and hospital info */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          {/* Hospital Info */}
          <Box display="flex" alignItems="flex-start">
            <Box mr={2}>
              <img src="/logo.jpeg" alt="Hospital Logo" style={{ height: 64 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">{hospitalInfo.name}</Typography>
              <Typography variant="body2">{hospitalInfo.address}</Typography>
              {hospitalInfo.phone && <Typography variant="body2">Phone: {hospitalInfo.phone}</Typography>}
              {hospitalInfo.email && <Typography variant="body2">Email: {hospitalInfo.email}</Typography>}
            </Box>
          </Box>
          {/* Invoice Details */}
          <Box textAlign="right" minWidth={220}>
            <Typography variant="subtitle1" fontWeight="bold">Invoice</Typography>
            <Typography variant="body2">Date: {appointment.date}</Typography>
            <Typography variant="body2">Patient: {appointment.patientName}</Typography>
            <Typography variant="body2">Doctor: {appointment.doctorName}</Typography>
            <Typography variant="body2">Appointment ID: {appointment.appointmentNumber}</Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        {/* Orders Table Grouped by Item Groups */}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedGroupNames.map((groupName) => (
              <React.Fragment key={`group-${groupName}`}>
                {/* Group Header */}
                <TableRow>
                  <TableCell colSpan={5} sx={{ 
                    backgroundColor: 'grey.100', 
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    py: 1
                  }}>
                    {groupName.toUpperCase()}
                  </TableCell>
                </TableRow>
                {/* Group Items */}
                {groupedOrders[groupName].map((order, idx) => (
                  <TableRow key={`${groupName}-${order.id || idx}`}>
                    <TableCell sx={{ pl: 3 }}>
                      {order.item?.itemName || order.item?.name || 'Item'}
                    </TableCell>
                    <TableCell align="right">{order.quantity}</TableCell>
                    <TableCell align="right">₹{order.price.toFixed(2)}</TableCell>
                    <TableCell align="right">₹{order.discountValue?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell align="right">₹{(order.price * order.quantity - (order.discountValue || 0)).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <Divider sx={{ my: 2 }} />
        {/* Payment summary */}
        <Box display="flex" justifyContent="flex-end" gap={4}>
          <Box>
            <Typography variant="body2">Total: ₹{total.toFixed(2)}</Typography>
            <Typography variant="body2">Paid: ₹{paid.toFixed(2)}</Typography>
            <Typography variant="body2">Due: ₹{due.toFixed(2)}</Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box mt={2} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Thank you for choosing {hospitalInfo.name}!
          </Typography>
        </Box>
      </Paper>
    );
  }
);

export default InvoicePrint;
